import { useState } from "react";
import { MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import useHomepageData from "../../hooks/user/useHomepageData";
import ComingSoonModal from "./ComingSoonModal";


export default function Footer() {
  const { categories, loading } = useHomepageData();
  const [comingSoonModal, setComingSoonModal] = useState({
    isOpen: false,
    featureName: "",
  });

  const handleComingSoon = (e, feature) => {
    e.preventDefault();
    setComingSoonModal({ isOpen: true, featureName: feature });
  };

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: null, featureName: "About Page" },
    { name: "Rentals", href: "/rentals" },
    // { name: "Events", href: "/events" },
    { name: "Gallery", href: "/gallery" },
    { name: "Insights", href: null, featureName: "Insights Blog" },
    { name: "Contact", href: "/contact" },
  ];

  const handleLinkClick = (e, link) => {
    if (link.featureName) {
      handleComingSoon(e, link.featureName);
    }
  };

  return (
    <footer
      className="bg-[#1A1A1A] text-white"
    >
      {/* CTA Section */}
      <div className="bg-gradient-to-br from-[#62397f] via-[#7C3AED] to-[#6D28D9] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-black/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-[1400px] mx-auto px-6 py-16 md:py-20">
          <div className="text-center">
            <h2
              className="text-white font-medium font-serif leading-tight mb-4 md:mb-6"
              style={{
                fontSize: "clamp(28px, 5vw, 48px)",
              }}
            >
              Ready to Create Your
              <br />
              Perfect Event?
            </h2>
            <p className="text-white/90 text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto px-4">
              Let's bring your vision to life with our premium rental collection
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <Link
                to="/rentals"
                className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-white hover:bg-gray-50 text-[#62397f] text-sm md:text-base font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(0,0,0,0.15)]"
              >
                Start Browsing
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-white/10 hover:bg-white/20 text-white text-sm md:text-base font-semibold rounded-full border-2 border-white/30 backdrop-blur-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-lg flex items-center justify-center">
                <img src="/static/images/logo.webp" alt="St. Stephen's Rentals logo" loading="eager" />
              </div>
              <div className="flex flex-col">
                <span className="font-serif text-xl font-bold leading-tight">
                  St. Stephen's
                </span>
                <span className="text-sm text-white leading-tight">
                  Rentals & Events
                </span>
              </div>
            </div>
            <p className="text-white text-sm leading-relaxed mb-6">
              Premium event rentals for unforgettable moments. Serving the
              community with excellence for over 15 years.
            </p>
            <div className="flex items-center gap-4">
              <Link
                to="https://www.instagram.com/st.stephens.rentals/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#2A2A2A] hover:bg-[#62397f] rounded-full flex items-center justify-center transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </Link>
              <Link
                to="https://www.facebook.com/p/Ststephens-events-100064674373060/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#2A2A2A] hover:bg-[#62397f] rounded-full flex items-center justify-center transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-base md:text-lg font-bold mb-4 md:mb-6 text-white">
              Quick Links
            </h3>
            <ul className="space-y-2 md:space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href || "#"}
                    onClick={(e) => handleLinkClick(e, link)}
                    className="text-white hover:text-[#62397f] transition-colors duration-150 text-sm md:text-base"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Rental Categories */}
          {(loading || categories.length > 0) && (
            <div className={`${categories.length > 6 ? "sm:col-span-2 lg:col-span-1" : ""}`}>
              <h3 className="font-serif text-base md:text-lg font-bold mb-4 md:mb-6 text-white">
                Rental Categories
              </h3>
              {loading ? (
                <div className="space-y-2 animate-pulse">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-4 bg-[#2A2A2A] rounded w-2/3" />
                  ))}
                </div>
              ) : (
                <ul className={`space-y-2 md:space-y-3 ${categories.length > 6 ? "grid grid-cols-2 gap-x-8" : ""}`}>
                  {categories.map((category, index) => (
                    <li key={index}>
                      <Link
                        to={`/rentals?category=${encodeURIComponent(category.name)}`}
                        className="text-white hover:text-[#62397f] transition-colors duration-150 text-sm md:text-base"
                      >
                        {category.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Contact Info */}
          <div>
            <h3 className="font-serif text-base md:text-lg font-bold mb-4 md:mb-6 text-white">
              Contact Us
            </h3>
            <ul className="space-y-3 md:space-y-4">
              <li className="flex items-start gap-3">
                <MapPin
                  size={18}
                  className="mt-1 text-[#62397f] flex-shrink-0"
                />
                <Link
                  to="https://maps.app.goo.gl/m9iAyeNt89fG56u46"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white text-sm md:text-base">
                  8 shaki crescent St, Surulere,
                  <br />
                  Lagos 101241, Lagos, Nigeria
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#62397f] flex-shrink-0" />
                <Link
                  to="tel:+23408023461907"
                  className="text-white hover:text-[#62397f] transition-colors duration-150 text-sm md:text-base"
                >
                  +234 (0)802 346 1907
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#62397f] flex-shrink-0" />
                <Link
                  to="mailto:info@ststephensrentals.com"
                  className="text-white hover:text-[#62397f] transition-colors duration-150 text-sm md:text-base break-all"
                >
                  info@ststephensrentals.com
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#333333]">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white text-xs md:text-sm text-center md:text-left">
              © {new Date().getFullYear()} St. Stephen's Rentals. All rights
              reserved.
            </p>
            <div className="flex items-center gap-4 md:gap-6">
              <Link
                to="/privacy"
                className="text-white hover:text-[#62397f] text-xs md:text-sm transition-colors duration-150"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-white hover:text-[#62397f] text-xs md:text-sm transition-colors duration-150"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, featureName: "" })}
        featureName={comingSoonModal.featureName}
      />
    </footer>
  );
}
